// ============================================================================
//  Documents GED — Page principale
// ============================================================================

import React, { useState, useRef, useEffect } from 'react'
import ReactDOM from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  FileText, FolderOpen, Folder, Plus, Search, Upload, Download,
  MoreVertical, Archive, Trash2, X, File, Image, FileSpreadsheet,
  FilePieChart, Grid, List, ChevronRight, Clock, User, Tag, Eye,
} from 'lucide-react'
import { documentsApi, type Document, type DocumentFolder, type DocumentSpace } from '@/modules/documents/api/documents.api'
import { FilePreviewModal, downloadFromUrl, errMessage } from '@/components/ui'
import { useNotificationStore } from '@/stores/notification.store'
import { useAuthStore } from '@/stores/auth.store'

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmtSize = (b: number) => b < 1024 ? `${b}B` : b < 1024*1024 ? `${(b/1024).toFixed(1)}KB` : `${(b/1024/1024).toFixed(1)}MB`
const fmtDate = (d: string) => new Date(d).toLocaleDateString('fr-SN', { day: '2-digit', month: 'short', year: 'numeric' })

const MIME_ICON: Record<string, React.ElementType> = {
  'application/pdf': FilePieChart,
  'image/':          Image,
  'application/vnd': FileSpreadsheet,
  'text/':           FileText,
}

function DocIcon({ mimeType, className = 'w-5 h-5' }: { mimeType: string; className?: string }) {
  const Icon = Object.entries(MIME_ICON).find(([k]) => mimeType.startsWith(k))?.[1] ?? File
  const colors: Record<string, string> = {
    'application/pdf': 'text-[var(--red-text)]',
    'image/':          'text-[var(--mauve)]',
    'application/vnd': 'text-[var(--green-text)]',
    'text/':           'text-[var(--blue-d)]',
  }
  const color = Object.entries(colors).find(([k]) => mimeType.startsWith(k))?.[1] ?? 'text-[var(--tx3)]'
  return <Icon className={`${className} ${color}`} />
}

function Spinner() { return <div className="w-5 h-5 border-2 border-[var(--blue)] border-t-transparent rounded-full animate-spin" /> }

// Libellés par espace : « Mes documents » (personnel) / « Documents partagés » (toute l'entité)
const SPACE_LABELS: Record<DocumentSpace, { title: string; subtitle: string; root: string }> = {
  mine:   { title: 'Mes documents',       subtitle: 'Espace personnel — visible uniquement par vous', root: 'Mes documents' },
  shared: { title: 'Documents partagés',  subtitle: 'Visibles par toute l’entreprise',                 root: 'Documents partagés' },
}

export default function DocumentsPage({ space = 'mine' }: { space?: DocumentSpace }) {
  const navigate = useNavigate()
  const [folderId, setFolderId] = useState<string | undefined>(undefined)
  const [folderPath, setFolderPath] = useState<{ id: string; name: string }[]>([])
  const [search, setSearch]     = useState('')
  const [fullText, setFullText] = useState(false)
  const [view, setView]         = useState<'grid' | 'list'>('list')
  const [showNewFolder, setShowNewFolder] = useState(false)
  // Aperçu : une seule visionneuse pour la page, sur le document sélectionné
  const [preview, setPreview]   = useState<Document | null>(null)
  const fileInputRef            = useRef<HTMLInputElement>(null)
  const addNotification         = useNotificationStore(s => s.addNotification)
  const queryClient             = useQueryClient()
  const { user }                = useAuthStore()
  const labels                  = SPACE_LABELS[space]

  // Changement d'espace (Mes documents ↔ Documents partagés) : retour à la racine
  useEffect(() => {
    setFolderId(undefined)
    setFolderPath([])
    setSearch('')
  }, [space])

  const { data: foldersData } = useQuery({
    queryKey: ['doc-folders', space, folderId],
    queryFn:  () => documentsApi.getFolders({ parentId: folderId, space }),
  })

  const { data: docsData, isLoading } = useQuery({
    queryKey: ['documents', space, folderId, search],
    queryFn:  () => documentsApi.getDocuments({ folderId, space, search: (!fullText && search) ? search : undefined }),
    enabled: !fullText || !search,
  })

  const { data: ftResults, isLoading: ftLoading } = useQuery({
    queryKey: ['documents-fulltext', space, search],
    queryFn:  () => documentsApi.fullTextSearch(search, 1, 20, space),
    enabled: fullText && search.length >= 2,
  })

  // Limites & types autorisés (alignés sur le backend documents.service.ts)
  const MAX_FILE_SIZE_MB = 50
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024
  const ALLOWED_EXTENSIONS = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'txt', 'csv', 'zip']
  const ACCEPT_ATTR = ALLOWED_EXTENSIONS.map(e => '.' + e).join(',')

  const uploadMutation = useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData()
      fd.append('file', file)
      // FIX: le DTO backend exige le champ 'name' (nom du document)
      fd.append('name', file.name.replace(/\.[^/.]+$/, '')) // nom sans extension
      return documentsApi.upload(fd, folderId, space)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      addNotification({ type: 'success', title: 'Fichier uploadé', message: '' })
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message
        ?? `Échec de l'upload — vérifiez le type (PDF, Office, images, ZIP) et la taille (${MAX_FILE_SIZE_MB} MB max).`
      addNotification({ type: 'error', title: 'Erreur upload', message: msg })
    },
  })

  const archiveMutation = useMutation({
    mutationFn: (id: string) => documentsApi.archive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      addNotification({ type: 'success', title: 'Archivé', message: '' })
    },
    onError: (err: any) => addNotification({ type: 'error', title: 'Archivage impossible', message: err?.response?.data?.message ?? 'Erreur inattendue' }),
  })

  const deleteDocMutation = useMutation({
    mutationFn: (id: string) => documentsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      queryClient.invalidateQueries({ queryKey: ['doc-folders'] })
      addNotification({ type: 'success', title: 'Document supprimé', message: '' })
    },
    onError: (err: any) => addNotification({ type: 'error', title: 'Suppression impossible', message: err?.response?.data?.message ?? 'Erreur inattendue' }),
  })

  const deleteFolderMutation = useMutation({
    mutationFn: (id: string) => documentsApi.deleteFolder(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['doc-folders'] })
      addNotification({ type: 'success', title: 'Dossier supprimé', message: '' })
    },
    // Message backend explicite : non vide (400) / pas le créateur (403)
    onError: (err: any) => addNotification({ type: 'error', title: 'Suppression impossible', message: err?.response?.data?.message ?? 'Dossier non vide ou non autorisé' }),
  })

  // Actions réservées à l'auteur d'un document (le backend applique la même règle)
  const ownerActions = (doc: Document) =>
    doc.uploadedBy?.id === user?.id
      ? {
          onArchive: () => { if (window.confirm(`Archiver "${doc.name}" ?`)) archiveMutation.mutate(doc.id) },
          onDelete:  () => { if (window.confirm(`Supprimer définitivement "${doc.name}" ?`)) deleteDocMutation.mutate(doc.id) },
        }
      : {}

  // FIX: getFolders retourne un tableau direct [], pas { folders: [] }
  const folders: DocumentFolder[] = Array.isArray(foldersData) ? foldersData : (foldersData?.folders ?? [])
  // FIX: getDocuments retourne { data: [], meta: {} } via paginate()
  const docs: Document[] = docsData?.data ?? docsData?.documents ?? []

  const navigateToFolder = (folder: DocumentFolder) => {
    setFolderId(folder.id)
    setFolderPath(prev => [...prev, { id: folder.id, name: folder.name }])
  }

  const navigateToBreadcrumb = (idx: number) => {
    if (idx === -1) { setFolderId(undefined); setFolderPath([]) }
    else { setFolderId(folderPath[idx].id); setFolderPath(folderPath.slice(0, idx + 1)) }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      // Validation client immédiate — évite les uploads inutiles
      const ext = file.name.split('.').pop()?.toLowerCase() ?? ''
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        addNotification({
          type: 'error',
          title: 'Type de fichier non supporté',
          message: `Formats acceptés : ${ALLOWED_EXTENSIONS.join(', ').toUpperCase()}`,
        })
        e.target.value = ''
        return
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        addNotification({
          type: 'error',
          title: 'Fichier trop volumineux',
          message: `Taille max : ${MAX_FILE_SIZE_MB} MB. Votre fichier : ${(file.size / 1024 / 1024).toFixed(1)} MB.`,
        })
        e.target.value = ''
        return
      }
      uploadMutation.mutate(file)
    }
    e.target.value = ''
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--tx)]">{labels.title}</h1>
          <p className="text-sm text-[var(--tx3)] mt-0.5">{labels.subtitle} · {docs.length} fichier{docs.length > 1 ? 's' : ''}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowNewFolder(true)}
            className="flex items-center gap-2 px-3 py-2 bg-[var(--surface-2)] hover:bg-[var(--surface-3)] text-[var(--tx)] rounded-lg text-sm font-medium transition-colors">
            <FolderOpen className="w-4 h-4" /> Dossier
          </button>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
            {uploadMutation.isPending ? <Spinner /> : <Upload className="w-4 h-4" />} Uploader
          </button>
          <input ref={fileInputRef} type="file" accept={ACCEPT_ATTR} className="hidden" onChange={handleFileChange} />
        </div>
      </div>

      {/* Caption — formats supportés */}
      <p className="text-xs text-[var(--tx3)]">
        Formats acceptés : PDF, Word, Excel, PowerPoint, images (JPG, PNG, GIF, WEBP), TXT, CSV, ZIP — Taille max : {MAX_FILE_SIZE_MB} MB
      </p>

      {/* Breadcrumb */}
      <div className="flex items-center gap-1 text-sm">
        <button onClick={() => navigateToBreadcrumb(-1)} className={`hover:text-[var(--tx)] transition-colors ${!folderId ? 'text-[var(--tx)] font-medium' : 'text-[var(--tx3)]'}`}>
          {labels.root}
        </button>
        {folderPath.map((f, i) => (
          <span key={f.id} className="flex items-center gap-1">
            <ChevronRight className="w-3.5 h-3.5 text-[var(--tx4)]" />
            <button onClick={() => navigateToBreadcrumb(i)}
              className={`hover:text-[var(--tx)] transition-colors ${i === folderPath.length - 1 ? 'text-[var(--tx)] font-medium' : 'text-[var(--tx3)]'}`}>
              {f.name}
            </button>
          </span>
        ))}
      </div>

      {/* Filtres + vues */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--tx3)]" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder={fullText ? "Rechercher dans le contenu des fichiers…" : "Rechercher un fichier…"}
            className="w-full pl-9 pr-4 py-2 bg-white border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] placeholder:text-[var(--tx3)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)]" />
        </div>
        <button onClick={() => setFullText(!fullText)}
          className={`px-3 py-2 rounded-lg text-xs font-medium border transition-colors whitespace-nowrap ${fullText ? 'bg-[var(--blue)] text-white border-[var(--blue)]' : 'bg-white text-[var(--tx3)] border-[var(--bd)] hover:border-[var(--blue)] hover:text-[var(--blue)]'}`}
          title="Rechercher dans le contenu des documents (PDF, DOCX, TXT)"
        >
          {fullText ? '✦ Contenu' : '☐ Contenu'}
        </button>
        <div className="flex border border-[var(--bd)] rounded-lg overflow-hidden">
          {(['list', 'grid'] as const).map(v => (
            <button key={v} onClick={() => setView(v)}
              className={`p-2 transition-colors ${view === v ? 'bg-[var(--surface-2)] text-[var(--tx)]' : 'bg-white text-[var(--tx3)] hover:text-[var(--tx)]'}`}>
              {v === 'list' ? <List className="w-4 h-4" /> : <Grid className="w-4 h-4" />}
            </button>
          ))}
        </div>
      </div>

      {/* Résultats recherche full-text */}
      {fullText && search.length >= 2 && (
        <div className="space-y-2">
          <p className="text-xs text-[var(--tx3)] font-medium">
            {ftLoading ? 'Recherche dans le contenu…' : `${ftResults?.data?.length ?? 0} résultat(s) dans le contenu`}
          </p>
          {(ftResults?.data ?? []).map((r: any) => (
            <div key={r.id} onClick={() => navigate(`/documents/${r.id}`)}
              className="bg-white border border-[var(--bd)] rounded-lg p-3 hover:border-[var(--blue)] cursor-pointer transition-colors">
              <div className="flex items-center gap-2 mb-1">
                <DocIcon mimeType={r.mimeType} className="w-4 h-4" />
                <span className="text-sm font-medium text-[var(--tx)]">{r.name}</span>
                <span className="text-xs text-[var(--tx3)]">{fmtSize(r.fileSize)}</span>
                <span className="text-xs text-[var(--tx3)] ml-auto">{r.uploader?.firstName} {r.uploader?.lastName}</span>
              </div>
              {r.highlight && (
                <p className="text-xs text-[var(--tx2)] line-clamp-2"
                  dangerouslySetInnerHTML={{ __html: r.highlight.replace(/<<(.*?)>>/g, '<mark class="bg-yellow-200 px-0.5 rounded">$1</mark>') }} />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Dossiers */}
      {folders.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {folders.map(f => (
            <div key={f.id} className="group relative flex flex-col items-center gap-2 p-4 bg-[var(--surface)] border border-[var(--bd)] rounded-xl hover:border-blue-500/30 hover:bg-[var(--surface-2)] transition-all">
              <button onClick={() => navigateToFolder(f)} className="flex flex-col items-center gap-2 w-full">
                <Folder className="w-8 h-8 text-[var(--amber-text)]" style={f.color ? { color: f.color } : {}} />
                <p className="text-xs text-[var(--tx)] text-center truncate w-full">{f.name}</p>
                {f._count && <p className="text-[10px] text-[var(--tx3)]">{f._count.documents} fichier{f._count.documents > 1 ? 's' : ''}</p>}
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); if (window.confirm(`Supprimer le dossier "${f.name}" ?`)) deleteFolderMutation.mutate(f.id) }}
                className="absolute top-2 right-2 p-1 opacity-0 group-hover:opacity-100 hover:bg-red-50 rounded text-[var(--tx4)] hover:text-red-500 transition-all"
                title="Supprimer"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Documents */}
      {isLoading ? (
        <div className="flex justify-center py-12"><Spinner /></div>
      ) : docs.length === 0 && folders.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-[var(--tx3)]">
          <FileText className="w-10 h-10 mb-3 opacity-20" />
          <p className="text-sm">Aucun fichier ici</p>
          <button onClick={() => fileInputRef.current?.click()} className="mt-2 text-xs text-[var(--blue-d)] hover:text-blue-300">
            Uploader votre premier fichier →
          </button>
        </div>
      ) : view === 'list' ? (
        <div className="bg-[var(--surface)] border border-[var(--bd)] rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--bd)]">
                {['Nom', 'Taille', 'Modifié', 'Par', ''].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-[var(--tx3)] uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--bd)]">
              {docs.map(doc => (
                <DocRow key={doc.id} doc={doc} {...ownerActions(doc)} onOpen={() => navigate(`/documents/${doc.id}`)} onPreview={() => setPreview(doc)} />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {docs.map(doc => <DocCard key={doc.id} doc={doc} {...ownerActions(doc)} onOpen={() => navigate(`/documents/${doc.id}`)} onPreview={() => setPreview(doc)} />)}
        </div>
      )}

      {/* Modal nouveau dossier */}
      {showNewFolder && (
        <NewFolderModal
          parentId={folderId}
          isShared={space === 'shared'}
          onClose={() => setShowNewFolder(false)}
          onCreated={() => {
            queryClient.invalidateQueries({ queryKey: ['doc-folders'] })
            setShowNewFolder(false)
          }}
        />
      )}

      {/* Visionneuse (lecture seule) */}
      <FilePreviewModal
        open={!!preview}
        onClose={() => setPreview(null)}
        fileName={preview?.name ?? ''}
        mimeType={preview?.mimeType}
        fileSize={preview?.fileSize}
        getInlineUrl={() => documentsApi.getDownloadUrl(preview!.id, 'inline')}
        getDownloadUrl={() => documentsApi.getDownloadUrl(preview!.id, 'attachment').then(r => r.url)}
      />
    </div>
  )
}

/** Téléchargement réel (URL « attachment ») ; `pending` évite le double clic, erreur en toast. */
function useDownloadDocument(doc: Document) {
  const addNotification = useNotificationStore(s => s.addNotification)
  const [pending, setPending] = useState(false)
  const download = async () => {
    if (pending) return
    setPending(true)
    try {
      const { url } = await documentsApi.getDownloadUrl(doc.id, 'attachment')
      downloadFromUrl(url)
    } catch (err: unknown) {
      addNotification({ type: 'error', title: 'Téléchargement impossible', message: errMessage(err, 'Erreur inattendue') })
    } finally {
      setPending(false)
    }
  }
  return { download, pending }
}

function DocRow({ doc, onArchive, onDelete, onOpen, onPreview }: { doc: Document; onArchive?: () => void; onDelete?: () => void; onOpen: () => void; onPreview: () => void }) {
  const [showMenu, setShowMenu] = useState(false)
  const { download, pending: downloading } = useDownloadDocument(doc)

  return (
    <tr className="hover:bg-[var(--surface-2)] transition-colors group">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <DocIcon mimeType={doc.mimeType} />
          <div>
            <button onClick={onOpen} className="text-sm text-[var(--tx)] font-medium hover:text-[var(--blue-d)] text-left transition-colors">{doc.name}</button>
            {doc.tags.length > 0 && (
              <div className="flex gap-1 mt-0.5">
                {doc.tags.slice(0, 3).map(t => <span key={t} className="text-[10px] text-[var(--tx3)]">#{t}</span>)}
              </div>
            )}
          </div>
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-[var(--tx3)]">{fmtSize(doc.fileSize)}</td>
      <td className="px-4 py-3 text-sm text-[var(--tx3)]">{fmtDate(doc.updatedAt)}</td>
      <td className="px-4 py-3">
        <span className="text-sm text-[var(--tx3)]">{doc.uploadedBy.firstName} {doc.uploadedBy.lastName[0]}.</span>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity">
          <button onClick={onPreview} className="p-1.5 hover:bg-[var(--surface-2)] rounded-lg text-[var(--tx3)] hover:text-[var(--tx)] transition-colors" title="Aperçu" aria-label={`Aperçu de ${doc.name}`}>
            <Eye className="w-4 h-4" aria-hidden="true" />
          </button>
          <button onClick={download} disabled={downloading} className="p-1.5 hover:bg-[var(--surface-2)] rounded-lg text-[var(--tx3)] hover:text-[var(--tx)] transition-colors disabled:opacity-40 disabled:cursor-wait" title="Télécharger" aria-label={`Télécharger ${doc.name}`}>
            <Download className="w-4 h-4" aria-hidden="true" />
          </button>
          {onArchive && (
            <button onClick={onArchive} className="p-1.5 hover:bg-[var(--surface-2)] rounded-lg text-[var(--tx3)] hover:text-[var(--amber-text)] transition-colors" title="Archiver">
              <Archive className="w-4 h-4" />
            </button>
          )}
          {onDelete && (
            <button onClick={onDelete} className="p-1.5 hover:bg-red-50 rounded-lg text-[var(--tx3)] hover:text-red-500 transition-colors" title="Supprimer définitivement">
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </td>
    </tr>
  )
}

function DocCard({ doc, onArchive, onDelete, onOpen, onPreview }: { doc: Document; onArchive?: () => void; onDelete?: () => void; onOpen: () => void; onPreview: () => void }) {
  const { download, pending: downloading } = useDownloadDocument(doc)
  return (
    <div className="group bg-[var(--surface)] border border-[var(--bd)] rounded-xl p-4 hover:border-blue-500/30 transition-all">
      <div className="flex justify-center mb-3">
        <DocIcon mimeType={doc.mimeType} className="w-10 h-10" />
      </div>
      <p className="text-xs text-[var(--tx)] text-center font-medium truncate mb-1">{doc.name}</p>
      <p className="text-[10px] text-[var(--tx3)] text-center">{fmtSize(doc.fileSize)}</p>
      <div className="flex justify-center gap-2 mt-3 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity">
        <button onClick={onOpen} className="p-1.5 hover:bg-[var(--surface-2)] rounded text-[var(--tx3)] hover:text-[var(--tx)]" title="Ouvrir">
          <FileText className="w-3.5 h-3.5" />
        </button>
        <button onClick={onPreview} className="p-1.5 hover:bg-[var(--surface-2)] rounded text-[var(--tx3)] hover:text-[var(--tx)]" title="Aperçu" aria-label={`Aperçu de ${doc.name}`}>
          <Eye className="w-3.5 h-3.5" aria-hidden="true" />
        </button>
        <button onClick={download} disabled={downloading} className="p-1.5 hover:bg-[var(--surface-2)] rounded text-[var(--tx3)] hover:text-[var(--tx)] disabled:opacity-40 disabled:cursor-wait" title="Télécharger" aria-label={`Télécharger ${doc.name}`}>
          <Download className="w-3.5 h-3.5" aria-hidden="true" />
        </button>
        {onArchive && (
          <button onClick={onArchive} className="p-1.5 hover:bg-[var(--surface-2)] rounded text-[var(--tx3)] hover:text-[var(--amber-text)]" title="Archiver">
            <Archive className="w-3.5 h-3.5" />
          </button>
        )}
        {onDelete && (
          <button onClick={onDelete} className="p-1.5 hover:bg-red-50 rounded text-[var(--tx3)] hover:text-red-500" title="Supprimer définitivement">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  )
}

function NewFolderModal({ parentId, isShared, onClose, onCreated }: { parentId?: string; isShared: boolean; onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('')
  const COLORS = ['#f59e0b', '#3b82f6', '#10b981', '#8b5cf6', '#ef4444', '#06b6d4']
  const [color, setColor] = useState(COLORS[0])
  const addNotification = useNotificationStore(s => s.addNotification)

  const { mutate, isPending } = useMutation({
    // Sans parent : l'espace est explicite ; avec parent, le backend hérite de l'espace du parent
    mutationFn: () => documentsApi.createFolder({ name, parentId, color, isShared }),
    onSuccess: onCreated,
    onError: (err: any) => addNotification({ type: 'error', title: 'Dossier non créé', message: err?.response?.data?.message ?? 'Erreur inattendue' }),
  })

  return (
    <div className="fixed inset-0 bg-[rgba(13,8,32,0.50)] backdrop-blur-sm flex items-center justify-center z-[99999] p-4">
      <div className="bg-white border border-[var(--bd)] rounded-2xl w-full max-w-lg shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-[var(--bd)]">
          <h2 className="text-base font-semibold text-[var(--tx)]">Nouveau dossier</h2>
          <button onClick={onClose}><X className="w-4 h-4 text-[var(--tx3)]" /></button>
        </div>
        <div className="p-5 space-y-4">
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Nom du dossier *"
            className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)]" />
          <div className="flex gap-2">
            {COLORS.map(c => (
              <button key={c} onClick={() => setColor(c)}
                className={`w-7 h-7 rounded-full transition-all ${color === c ? 'ring-2 ring-[var(--brand-orange)] ring-offset-2 ring-offset-slate-800 scale-110' : 'hover:scale-110'}`}
                style={{ background: c }} />
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-3 p-5 border-t border-[var(--bd)]">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[var(--tx3)] hover:text-[var(--tx)]">Annuler</button>
          <button onClick={() => mutate()} disabled={isPending || !name}
            className="flex items-center gap-2 px-5 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm font-medium disabled:opacity-50">
            {isPending ? <div className="w-4 h-4 border-2 border-[var(--bd2)] border-t-white rounded-full animate-spin" /> : <Plus className="w-4 h-4" />}
            Créer
          </button>
        </div>
      </div>
    </div>
  )
}
